import { useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  linkOptions,
  Outlet,
  useLocation,
} from "@tanstack/react-router";
import { Key, Mail, Pencil, Type } from "lucide-react";
import { useMemo } from "react";
import { canModifyRole, capitalize, formatDate, formatUSPhone } from "shared";
import { createColumnConfigHelper } from "@/components/data-table-filter/core/filters";
import { IconButton } from "@/components/icon-button";
import { IconText } from "@/components/icon-text";
import { RenderStatusBadge } from "@/components/table/cells/RenderStatusBadge";
import { DataTable } from "@/components/table/DataTable";
import { TablePagination } from "@/components/table/DataTablePagination";
import type { ColumnDefWithMeta } from "@/components/table/dataTable.types";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { UserAvatar } from "@/components/user-avatar";
import { usePersistedReactTable } from "@/hooks/use-persisted-table";
import { useRequiredRole } from "@/hooks/useRequiredRole";
import { authClient } from "@/lib/authClient";
import type { RouterOutputs } from "@/lib/trpcClient";

export const Route = createFileRoute("/_authed/_org/organization/people/all")({
  component: RouteComponent,
});

type UserType = RouterOutputs["org"]["users"]["users"][number];
const dtf = createColumnConfigHelper<UserType>();
const tableName = "people";
function RouteComponent() {
  const { trpc } = Route.useRouteContext();
  const {
    data: { user },
  } = useSuspenseQuery(trpc.auth.session.queryOptions());
  const { admin, owner } = useRequiredRole();

  const tableColumns: ColumnDefWithMeta<
    RouterOutputs["org"]["users"]["users"][number]
  >[] = [
    {
      accessorFn: (row) => row.name,
      accessorKey: "Name",
      cell: ({ row }) => (
        <div className="flex items-center justify-between">
          <UserAvatar userId={row.original.id} className="mr-2" />

          {user?.role === "admin" && (
            <IconButton
              icon="user-round-search"
              tooltip="Impersonate User"
              onClick={async () => {
                await authClient.admin.impersonateUser({
                  fetchOptions: {
                    onSuccess: () => window.location.reload(),
                  },
                  userId: row.original.id,
                });
              }}
            />
          )}
        </div>
      ),
      enableGlobalFilter: true,
      enableSorting: true,
      meta: {
        type: "string",
      },
    },
    {
      accessorFn: (row) => row.role,
      accessorKey: "Role",
      cell: ({ row }) => (
        <div
          className="flex flex-row justify-between"
          data-role={row.original.role}
        >
          <RenderStatusBadge status={row.original.role}>
            {row.original.role === "none" ? "Deactivated" : row.original.role}
          </RenderStatusBadge>

          {row.original.role !== "owner" &&
            row.original.role !== "none" &&
            canModifyRole({
              admin,
              owner,
              toModifyRole: row.original.role,
            }) && (
              <Link
                to="/organization/people/all/$id/edit-role"
                params={{
                  id: row.original.id,
                }}
              >
                <Pencil className="size-4" />
              </Link>
            )}
        </div>
      ),
      id: "Role",
      meta: {
        type: "select",
      },
    },
    {
      accessorFn: (row) => row.email,
      accessorKey: "Email",
      enableGlobalFilter: true,
      enableSorting: true,
      id: "Email",
      meta: {
        type: "string",
      },
    },
    {
      accessorFn: (row) => formatUSPhone(row.phone),
      accessorKey: "Phone",
      cell: ({ row }) => (
        <IconText className="text-neutral-700 [&>svg]:mr-3" icon="phone">
          {formatUSPhone(row.original.phone)}
        </IconText>
      ),
      id: "Phone",
      meta: {
        type: "string",
      },
    },
    {
      accessorFn: (row) => formatDate(row.joined),
      id: "Joined",
      meta: {
        type: "string",
      },
    },
  ];
  const { data: orgUsers } = useSuspenseQuery(trpc.org.users.queryOptions());

  const data = useMemo(() => orgUsers.users, [orgUsers.users]);

  const columnsConfig = [
    dtf
      .text()
      .id("Name")
      .accessor((row) => row.name)
      .displayName("Name")
      .icon(Type)
      .build(),
    dtf
      .text()
      .id("Email")
      .accessor((row) => row.email)
      .displayName("Email")
      .icon(Mail)
      .build(),
    dtf
      .option()
      .id("Role")
      .accessor((row) => row.role)
      .displayName("Role")
      .transformOptionFn((u) => ({
        label: capitalize(u),
        value: u,
      }))
      .icon(Key)
      .build(),
  ];

  const { table, actions } = usePersistedReactTable({
    columns: tableColumns,
    data,
    filterOptions: {
      columnsConfig,
      strategy: "client",
    },
    getRowId: (row) => row.id.toString(),
    initialState: {
      columnVisibility: {
        Email: false,
      },
    },
    tableName,
  });

  const navigate = Route.useNavigate();
  const location = useLocation();

  return (
    <div className="flex h-screen flex-col">
      <Sheet
        open={
          location.pathname !== "/organization/people/all/" &&
          location.pathname.includes("/organization/people/all/")
        }
        onOpenChange={(open) => {
          if (!open) {
            navigate({
              replace: true,
              to: "/organization/people/all",
            });
          }
        }}
      >
        <SheetContent>
          <Outlet />
        </SheetContent>
        <DataTable
          noResultsComponent={
            <div className="flex h-36 w-full flex-col items-center justify-center space-y-4">
              <span className="text-xl">No Matching Users</span>

              <Button onClick={actions?.removeAllFilters} variant={"outline"}>
                Clear Filters
              </Button>
            </div>
          }
          table={table}
          tableName={tableName}
          navOptions={
            admin
              ? (row) =>
                  linkOptions({
                    params: {
                      id: row.original.id,
                    },
                    to: "/organization/people/all/$id",
                  })
              : undefined
          }
          className="[&_th]:border-t-0! [&_tr:has(div[data-role='none'])_td]:opacity-50"
        />
        <TablePagination tableName={tableName} table={table} />
      </Sheet>
    </div>
  );
}
